/**
 * Authenticated fetch for QC Lite → backend (`VITE_APP_API_BACKEND_URL`).
 *
 * Matches Auth0 React SPA guidance: obtain an access token with
 * `getAccessTokenSilently()` and send `Authorization: Bearer <token>`.
 * @see https://auth0.com/docs/quickstart/spa/react (Calling Protected APIs)
 */

import { getAuth0TokenAuthorizationParams } from '../auth/auth0TokenParams';

const API_BASE = (import.meta.env.VITE_APP_API_BACKEND_URL ?? '').replace(
  /\/$/,
  '',
);

/**
 * Resolve a path or absolute URL for API requests.
 * @param {string | URL | Request} input
 * @returns {string | URL | Request}
 */
export function resolveApiUrl(input) {
  if (typeof input !== 'string') return input;
  if (/^https?:\/\//i.test(input)) return input;
  const path = input.startsWith('/') ? input : `/${input}`;
  return API_BASE ? `${API_BASE}${path}` : path;
}

/**
 * @param {() => Promise<string>} getAccessTokenSilently - from `useAuth0()`
 * @param {string | URL | Request} input
 * @param {RequestInit} [init]
 * @param {{ skipAuth?: boolean; getTokenOptions?: Record<string, unknown> }} [options]
 */
export async function authFetch(
  getAccessTokenSilently,
  input,
  init = {},
  options = {},
) {
  const { skipAuth = false, getTokenOptions } = options;
  const resolved = resolveApiUrl(input);

  const headers = new Headers(init.headers ?? {});

  if (!skipAuth) {
    if (typeof getAccessTokenSilently !== 'function') {
      throw new Error(
        'authFetch: getAccessTokenSilently is required when skipAuth is false',
      );
    }
    let token;
    try {
      token = await getAccessTokenSilently({
        ...getTokenOptions,
        authorizationParams: {
          ...getAuth0TokenAuthorizationParams(),
          ...getTokenOptions?.authorizationParams,
        },
      });
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[authFetch] failed to acquire access token', err);
      }
      throw err;
    }
    headers.set('Authorization', `Bearer ${token}`);
  }

  return fetch(resolved, {
    ...init,
    headers,
  });
}

/**
 * JSON helpers — same auth behavior as `authFetch`.
 */
export async function authGetJson(
  getAccessTokenSilently,
  path,
  init = {},
  options,
) {
  const res = await authFetch(getAccessTokenSilently, path, init, options);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Request failed: ${res.status}`);
  }
  return res.json();
}

export async function authSendJson(
  getAccessTokenSilently,
  path,
  { method = 'POST', body, ...rest } = {},
  options,
) {
  const headers = new Headers(rest.headers ?? {});
  if (body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  let serializedBody = body;
  if (body !== undefined && typeof body !== 'string') {
    try {
      serializedBody = JSON.stringify(body);
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[authSendJson] failed to serialize body', err);
      }
      throw err;
    }
  }
  const init = {
    ...rest,
    method,
    headers,
    body: serializedBody,
  };
  return authFetch(getAccessTokenSilently, path, init, options);
}
